import { OverlayProvider } from '@aigc/overlay-hook';
import { Outlet, createRootRoute, redirect } from '@tanstack/react-router';
import { App as AntdApp } from 'antd';
import { jwtDecode, type JwtPayload } from 'jwt-decode';
import { redirectToLoginPage, refreshToken } from '../features/auth/api';
import {
  clearAccessToken,
  getAccessToken,
  isAccessTokenExpiringSoon,
  setAccessToken,
} from '../features/auth/utils';
import { GlobalErrorHandler } from '../features/common/components/global-error-handler';
import { NotFoundPage } from '../features/common/components/not-found-page';

const Root = () => {
  return (
    <OverlayProvider>
      {(nodes) => (
        <AntdApp
          className="h-full w-full"
          notification={{
            stack: {
              threshold: 2,
            },
            duration: 3,
            maxCount: 10,
            rtl: false,
          }}
        >
          <GlobalErrorHandler>
            {nodes}
            <Outlet />
          </GlobalErrorHandler>
        </AntdApp>
      )}
    </OverlayProvider>
  );
};

export const Route = createRootRoute({
  component: Root,
  beforeLoad: async ({ search, params, location }) => {
    if ('access_token' in search) {
      const { access_token, ...restSearch } = search;

      setAccessToken(search.access_token as string);

      try {
        const payload = jwtDecode(access_token as string);

        lfq('init', window['trackId'], { user_id: payload.name });
      } catch (error) {
        console.error(error);
      }

      return redirect({
        to: location.pathname,
        search: restSearch,
        params: params,
      });
    }

    window.addEventListener('storage', (ev) => {
      if (ev.key === 'access_token') {
        setupToken();
      }
    });

    await setupToken();
  },
  notFoundComponent: NotFoundPage,
});

const setupToken = async () => {
  let access_token = getAccessToken();

  if (!access_token) {
    redirectToLoginPage();
    return;
  }

  let payload: JwtPayload;

  try {
    payload = jwtDecode(access_token);
  } catch {
    clearAccessToken();
    redirectToLoginPage();
    return;
  }

  try {
    if (isAccessTokenExpiringSoon(payload.exp)) {
      access_token = await refreshToken();
      setAccessToken(access_token);
    }
  } catch (error) {
    console.error(error);
    redirectToLoginPage();
  }

  autoRefreshToken(access_token);
};

let timer = 0;

const autoRefreshToken = (access_token: string) => {
  const payload = jwtDecode(access_token);

  const timeout =
    (payload.exp ?? 0) * 1000 - Date.now() - Math.random() * 60_000;

  window.clearTimeout(timer);
  timer = window.setTimeout(async () => {
    const revalidateAndRefreshToken = async () => {
      const currentAccessToken = getAccessToken();

      if (!currentAccessToken) {
        return;
      }

      const payload = jwtDecode(currentAccessToken);

      if (!isAccessTokenExpiringSoon(payload.exp)) {
        autoRefreshToken(currentAccessToken);
        return;
      }

      const newAccessToken = await refreshToken();
      setAccessToken(newAccessToken);
      autoRefreshToken(newAccessToken);
    };

    const locks = navigator.locks as Navigator['locks'] | undefined;

    if (locks) {
      // 使用锁来避免重复刷新 Token
      await navigator.locks.request('access_token', revalidateAndRefreshToken);
    } else {
      await revalidateAndRefreshToken();
    }
  }, timeout);
};

declare module 'jwt-decode' {
  interface JwtPayload {
    name?: string;
  }
}
